import React, { useState, useEffect } from 'react';
import {
  AttributeValue,
  AttributeVariable,
} from '../../../../api/protosCompiled/globalItem/globalItem_pb';
import { useMutation, useQueryClient } from 'react-query';
import { ItemService } from '../../../../api/GlobalItemService';
import { useFetchAllowedVariables } from '../../../../hooks/useFetchAllowedVariables';
import {
  Box,
  Button,
  Typography,
  TextField,
  Select,
  MenuItem,
  IconButton,
  Paper,
  FormControl,
  InputLabel,
  useTheme,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';

interface ManageAttributeDialogProps {
  isOpen: boolean;
  onClose: () => void;
  attributeValue: AttributeValue.AsObject | null;
}

const ManageAttributeDialog: React.FC<ManageAttributeDialogProps> = ({
  isOpen,
  onClose,
  attributeValue,
}) => {
  const queryClient = useQueryClient();
  const { data: allowedVariables } = useFetchAllowedVariables();
  const theme = useTheme();

  const [attributeName, setAttributeName] = useState<string>('');
  const [groupId, setGroupId] = useState<number>(0);
  const [variables, setVariables] = useState<AttributeVariable.AsObject[]>([]);

  useEffect(() => {
    if (attributeValue) {
      setAttributeName(attributeValue.attributevaluename);
      setGroupId(attributeValue.attributegroupid || 0);
      setVariables(attributeValue.attributevariablesList || []);
    } else {
      setAttributeName('');
      setGroupId(0);
      setVariables([]);
    }
  }, [attributeValue]);

  const { mutateAsync: createOrUpdateAttributeValueMutate } = useMutation(
    ItemService.createOrUpdateAttributeValue,
    {
      onSuccess: () => queryClient.invalidateQueries('attributeGroups'),
    }
  );

  const { mutateAsync: deleteAttributeVariableMutate } = useMutation(
    ItemService.deleteAttributeVariables,
    {
      onSuccess: () => queryClient.invalidateQueries('attributeGroups'),
    }
  );

  const handleSave = async () => {
    if (!attributeName) {
      alert('Attribute name is required.');
      return;
    }

    if (!groupId) {
      alert('Attribute group ID is missing. Please ensure a valid group is selected.');
      return;
    }

    const updatedAttributeValue: AttributeValue.AsObject = {
      itemattributevaluemapid: attributeValue?.itemattributevaluemapid || 0,
      attributevalueid: attributeValue?.attributevalueid || 0,
      attributevaluename: attributeName,
      attributevariablesList: variables,
      attributegroupid: groupId,
      attributegroupname: attributeValue?.attributegroupname || '',
      upc: attributeValue?.upc || '',
    };

    try {
      await createOrUpdateAttributeValueMutate(updatedAttributeValue);
      onClose();
    } catch (error) {
      console.error('Error saving attribute value:', error);
    }
  };

  const addVariable = () => {
    if (!allowedVariables || allowedVariables.length === 0) {
      alert('No allowed variables available.');
      return;
    }

    const usedVariableIds = variables.map(v => v.variableid);
    const availableVariables = allowedVariables.filter(av => !usedVariableIds.includes(av.id));

    if (availableVariables.length === 0) {
      alert('No more variables available to add.');
      return;
    }

    const newVariable: AttributeVariable.AsObject = {
      itemattributevaluevariablemapid: 0,
      variableid: availableVariables[0].id,
      variable: availableVariables[0],
      variablevalue: 0,
    };

    setVariables(prevVariables => [...prevVariables, newVariable]);
  };

  const updateVariable = (
    index: number,
    key: keyof AttributeVariable.AsObject,
    value: AttributeVariable.AsObject[keyof AttributeVariable.AsObject]
  ) => {
    setVariables(prevVariables => {
      const updatedVariables = [...prevVariables];
      updatedVariables[index] = {
        ...updatedVariables[index],
        [key]: value,
      };
      return updatedVariables;
    });
  };

  const removeVariable = async (variable: AttributeVariable.AsObject) => {
    if (variable.itemattributevaluevariablemapid) {
      try {
        await deleteAttributeVariableMutate(variable.itemattributevaluevariablemapid);
      } catch (error) {
        console.error('Error deleting variable:', error);
        return;
      }
    }
    setVariables(variables.filter(v => v !== variable));
  };

  /**
   * Returns only variables that are not yet used,
   * plus the one that's currently selected (so it doesn't disappear from the <select>).
   */
  const getAvailableVariables = (currentVariableId: number) => {
    const usedVariableIds = variables.map(v => v.variableid);
    return allowedVariables?.filter(
      av => av.id === currentVariableId || !usedVariableIds.includes(av.id)
    );
  };

  if (!isOpen) return null;

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        maxHeight: 'calc(90vh - 64px)', // Ensure dialog content respects viewport
        display: 'flex',
        flexDirection: 'column',
        bgcolor: theme.palette.background.paper,
        color: theme.palette.text.primary,
      }}
    >
      {/* Header */}
      <Box
        sx={{
          p: 2,
          borderBottom: `1px solid ${theme.palette.divider}`,
          bgcolor: theme.palette.background.paper,
          flexShrink: 0, // Prevent header from shrinking
        }}
      >
        <Typography variant="h6" align="center" component="h2">
          {attributeValue?.attributevalueid ? 'Edit Attribute' : 'Create Attribute'}
        </Typography>
      </Box>

      {/* Main Content (fixed, not scrollable) */}
      <Box
        sx={{
          p: 2,
          borderBottom: `1px solid ${theme.palette.divider}`,
          flexShrink: 0, // Prevent section from shrinking
        }}
      >
        {/* Attribute Name */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
            Attribute Name
          </Typography>
          <TextField
            fullWidth
            size="small"
            value={attributeName}
            onChange={e => setAttributeName(e.target.value)}
            sx={{
              '& .MuiOutlinedInput-root': {
                bgcolor: theme.palette.background.default,
              },
            }}
            inputProps={{
              'aria-label': 'Attribute name',
            }}
          />
        </Box>
      </Box>

      {/* Variables Section (scrollable) */}
      <Box
        sx={{
          p: 2,
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          overflowY: 'hidden',
          minHeight: 0, // This is critical for proper flex behavior with overflow
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 1,
            flexShrink: 0, // Prevent shrinking
          }}
        >
          <Typography variant="subtitle1" fontWeight="bold">
            Variables
          </Typography>
          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon />}
            onClick={addVariable}
            aria-label="Add variable"
          >
            Add Variable
          </Button>
        </Box>

        {/* Variables Table - THIS IS THE ONLY SCROLLABLE AREA */}
        <Paper
          variant="outlined"
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            bgcolor: theme.palette.background.default,
            overflow: 'hidden',
            minHeight: 0, // Critical for nested flex containers with scrolling
          }}
        >
          <TableContainer
            sx={{
              flex: 1,
              overflowY: 'auto',
              maxHeight: '100%',
            }}
          >
            <Table
              size="small"
              aria-label="Variables table"
              stickyHeader
              sx={{
                '& .MuiTableCell-root': {
                  padding: '8px 16px',
                  color: theme.palette.text.primary,
                  borderColor: theme.palette.divider,
                },
                '& .MuiTableHead-root': {
                  '& .MuiTableCell-root': {
                    bgcolor: 'inherit',
                    color: theme.palette.text.primary,
                    fontWeight: 600,
                  },
                },
              }}
            >
              <TableHead>
                <TableRow
                  sx={{
                    bgcolor:
                      theme.palette.mode === 'dark' ? '#2c2c2c !important' : '#f5f5f5 !important',
                  }}
                >
                  <TableCell width="50%">Variable</TableCell>
                  <TableCell width="35%">Value</TableCell>
                  <TableCell width="15%" align="center">
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {variables.length === 0 ? (
                  <TableRow
                    sx={{
                      bgcolor:
                        theme.palette.mode === 'dark' ? '#2c2c2c !important' : '#f5f5f5 !important',
                    }}
                  >
                    <TableCell colSpan={3} align="center" sx={{ bgcolor: 'inherit !important' }}>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        align="center"
                        sx={{ py: 2 }}
                      >
                        No variables added yet. Click "Add Variable" to begin.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  variables.map((v, index) => (
                    <TableRow
                      key={index}
                      sx={{
                        bgcolor:
                          theme.palette.mode === 'dark'
                            ? index % 2 === 0
                              ? '#2c2c2c !important'
                              : '#3c3c3c !important'
                            : index % 2 === 0
                              ? '#f5f5f5 !important'
                              : '#e0e0e0 !important',
                        '&:hover': {
                          bgcolor: `${
                            theme.palette.mode === 'dark'
                              ? 'rgba(33, 150, 243, 0.1)'
                              : 'rgba(33, 150, 243, 0.1)'
                          } !important`,
                        },
                      }}
                    >
                      <TableCell>
                        <FormControl fullWidth size="small">
                          <InputLabel id={`variable-label-${index}`}>Variable</InputLabel>
                          <Select
                            labelId={`variable-label-${index}`}
                            value={v.variableid}
                            label="Variable"
                            onChange={e => {
                              const selectedVariable = allowedVariables?.find(
                                av => av.id === parseInt(e.target.value as string)
                              );
                              if (selectedVariable) {
                                updateVariable(index, 'variable', selectedVariable);
                                updateVariable(index, 'variableid', selectedVariable.id);
                              }
                            }}
                            sx={{
                              bgcolor:
                                theme.palette.mode === 'dark'
                                  ? 'rgba(44, 44, 44, 0.9)'
                                  : theme.palette.background.paper,
                            }}
                          >
                            {getAvailableVariables(v.variableid)?.map(av => (
                              <MenuItem key={av.id} value={av.id}>
                                {av.name}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </TableCell>
                      <TableCell>
                        <TextField
                          fullWidth
                          size="small"
                          label="Value"
                          type="number"
                          value={v.variablevalue}
                          onChange={e =>
                            updateVariable(index, 'variablevalue', parseFloat(e.target.value))
                          }
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              bgcolor:
                                theme.palette.mode === 'dark'
                                  ? 'rgba(44, 44, 44, 0.9)'
                                  : theme.palette.background.paper,
                            },
                          }}
                          inputProps={{
                            'aria-label': `Value for ${v.variable?.name || 'variable'}`,
                          }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <IconButton
                          onClick={() => removeVariable(v)}
                          aria-label={`Remove ${v.variable?.name || 'variable'}`}
                          size="small"
                          color="error"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Box>

      {/* Footer */}
      <Box
        sx={{
          p: 2,
          borderTop: `1px solid ${theme.palette.divider}`,
          bgcolor: theme.palette.background.paper,
          textAlign: 'right',
          flexShrink: 0, // Prevent footer from shrinking
        }}
      >
        <Button
          variant="outlined"
          onClick={onClose}
          sx={{ mr: 1 }}
          aria-label="Cancel and close dialog"
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          color="success"
          onClick={handleSave}
          aria-label="Save attribute"
        >
          Save
        </Button>
      </Box>
    </Box>
  );
};

export default React.memo(ManageAttributeDialog);
